import axios from '../axios';
import { defineStore } from 'pinia';
import { useAssetsStore } from '@/stores/assets';
import { useBranchesStore } from '@/stores/branches';
import { useAppStore } from '@/stores/index';

export const useAuthStore = defineStore('auth', {
    state: () => {
        return {
            user: null,
            company: null,
            company_id: null,
            allowed_branches: null,
            permissions: [],
            branch_id: Number(localStorage.getItem('branch_id')),
            login_by: localStorage.getItem('login_by') || 'email',
            login_country_iso: localStorage.getItem('login_country_iso') || 'PS',
            requested: false,
            unauthorized: false,
            currencies: null
        };
    },
    actions: {
        async login(credentials) {
            await axios.get('/sanctum/csrf-cookie');

            await axios.post('/login', credentials);

            await this.loadUser(true);
            await useAssetsStore().loadAssets(true);
            await useBranchesStore().loadBranches(true);

            this.unauthorized = false;
        },
        async signup(data) {
            // reset branch_id
            this.branch_id = null;

            await axios.get('/sanctum/csrf-cookie');

            await axios.post('/register', data);

            await this.loadUser(true);
            await useAssetsStore().loadAssets(true);
            await useBranchesStore().loadBranches(true);

            this.unauthorized = false;
        },
        async forgetPassword(email: string) {
            return await axios.post('/forgot-password', { email });
        },
        async resetPassword(data: object) {
            return await axios.post('/reset-password', data);
        },
        logout() {
            const self = this;
            axios.post('/logout').finally(() => {
                self.loggedOut();
            });
        },
        loggedOut() {
            this.user = null;

            if (!window.location.pathname.startsWith('/auth')) {
                this.router.push('/auth/login');
            }
        },
        async loadUser(refresh: boolean = false) {
            if (!this.requested || refresh) {
                try {
                    const user = (await this.request().get('api/user')).data;
                    this.user = user;
                    this.company_id = user.companies[0].id;
                    this.company = user.companies[0];
                    this.allowed_branches = this.company.branch_permissions?.map((bp) => bp.branch_id);

                    if (!this.branch_id) {
                        if (this.company.user_role === 'owner') {
                            this.setBranchId(1);
                        } else {
                            this.setBranchId(this.allowed_branches[0]);
                        }
                    } else if (this.allowed_branches && !this.allowed_branches.includes(this.branch_id)) {
                        this.setBranchId(this.allowed_branches[0]);
                    } else {
                        this.permissions = this.company.branch_permissions?.find(bp => bp.branch_id === this.branch_id)?.permissions || [];
                    }
                    this.requested = true;

                    // add navbar buttons
                    const store = useAppStore();
                    store.navbarButtons = user.navbar_buttons;
                } catch (error) {

                }
            }
        },
        async setBranchId(id, reloadAssets: boolean = true) {
            if (this.branch_id !== Number(id)) {
                localStorage.setItem('branch_id', id);

                this.branch_id = Number(id);

                this.permissions = this.company.branch_permissions?.find(bp => bp.branch_id === Number(id))?.permissions || [];

                if (reloadAssets) {
                    await useAssetsStore().loadAssets(true);
                }
            }
        },
        setLoginBy(by: string) {
            localStorage.setItem('login_by', by);

            this.login_by = by;
        },
        setLoginCountryIso(countryIso: string) {
            localStorage.setItem('login_country_iso', countryIso);

            this.login_country_iso = countryIso;
        },
        request(withBranch: boolean = true) {
            axios.defaults.headers.common['X-Company-Id'] = this.company_id;
            if (withBranch) {
                axios.defaults.headers.common['X-Branch-Id'] = this.branch_id;
            }

            return axios;
        },
        async getCurrencies() {
            if (!this.currencies) {
                this.currencies = (await this.request().get('api/currencies')).data;
            }

            return this.currencies;
        },
        can(permission: string) {
            const branch = useBranchesStore().currentBranch;

            const userPermissionsMap = {
                'other_orders_currency_exchange': 'other_orders',
                'other_orders_match_weights': 'other_orders',
                'other_orders_expenses': 'other_orders',
                'other_orders_set_cash_balance': 'other_orders',
                'other_orders_convert_gold_type': 'other_orders'
            };

            return (!branch.features || !branch.features[permission] || branch.features[permission].status) &&
                (!this.permissions || this.permissions.length === 0 || this.permissions.includes(userPermissionsMap[permission] || permission));
        },
        canAny(permissions: Array<string>) {
            return permissions.some(permission => this.can(permission));
        },
        isOwner() {
            return this.company.user_role === 'owner';
        },
        isBranchAdmin() {
            return this.user.id == useBranchesStore().currentBranch.manager_id;
        }
    },
    getters: {
        authenticated: state => !!state.user
    }
});
